// React Query hooks for the cross-tenant super module.
//
// Reads land in the `['super', '...']` cache namespace. Mutations invalidate
// `['super', 'companies']` so list + detail pages refresh after a tier /
// status change.

import { useNavigate } from 'react-router-dom';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { useToast } from '../../../hooks/useToast';
import { useAuthStore } from '../../../store/authStore';
import { useSuperAdminStore } from '../../../store/superAdminStore';
import type { MeResult } from '../../../types/api';

export interface SuperCompanyListItem {
  enc_id: string;
  /** Raw numeric id — used by viewers that filter by numeric company_id
   *  (e.g. the WB Activity log) rather than the encrypted enc_id. */
  id: number;
  name: string;
  subdomain: string | null;
  tier: string | null;
  tier_name: string | null;
  status: 'active' | 'inactive' | 'suspended';
  subscription_status: 'trial' | 'active' | 'suspended' | 'cancelled';
  staff_count: number;
  driver_count: number;
  booking_count_30d: number;
  created_at: string;
  deleted_at: string | null;
}

export interface SuperCompanyDetail {
  enc_id: string;
  id: number;
  uuid: string;
  name: string;
  subdomain: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  postcode: string | null;
  tier: string | null;
  tier_key: string | null;
  tier_name: string | null;
  status: 'active' | 'inactive' | 'suspended';
  subscription_status: 'trial' | 'active' | 'suspended' | 'cancelled';
  staff_count: number;
  driver_count: number;
  active_driver_count: number;
  booking_count_total: number;
  booking_count_30d: number;
  receipt_count_total: number;
  message_queue_pending: number;
  message_queue_failed: number;
  last_booking_at: string | null;
  last_login_at: string | null;
  created_at: string;
  deleted_at: string | null;
}

export interface SystemHealth {
  message_queue: {
    pending: number;
    processing: number;
    failed: number;
    sent_24h: number;
    skipped_24h: number;
  };
  crons: {
    notification_last_run: string | null;
    reminder_last_run: string | null;
    cleanup_last_run: string | null;
  };
  companies: {
    total: number;
    active: number;
    suspended: number;
    trial: number;
  };
  server: {
    uptime_seconds: number;
    node_version: string;
    timestamp: string;
  };
}

export interface ListCompaniesFilters {
  page?: number;
  limit?: number;
  status?: 'active' | 'inactive' | 'suspended';
  tier?: string;
  search?: string;
}

interface ListCompaniesResponse {
  companies: SuperCompanyListItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export function useAllCompanies(filters: ListCompaniesFilters = {}) {
  const params = new URLSearchParams();
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.status !== undefined) params.set('status', filters.status);
  if (filters.tier !== undefined && filters.tier.length > 0) params.set('tier', filters.tier);
  if (filters.search !== undefined && filters.search.length > 0) {
    params.set('search', filters.search);
  }
  const qs = params.toString();
  const opts: UseQueryOptions<ListCompaniesResponse> = {
    queryKey: ['super', 'companies', filters],
    queryFn: async () => {
      const res = await apiClient.get<ListCompaniesResponse>(
        `/super/companies${qs ? `?${qs}` : ''}`,
      );
      return res.data;
    },
    staleTime: 30_000,
  };
  return useQuery(opts);
}

export function useCompanyDetail(encId: string | undefined) {
  return useQuery<SuperCompanyDetail>({
    queryKey: ['super', 'companies', 'detail', encId],
    queryFn: async () => {
      if (!encId) throw new Error('enc_id required');
      const res = await apiClient.get<{ company: SuperCompanyDetail }>(
        `/super/companies/${encodeURIComponent(encId)}`,
      );
      return res.data.company;
    },
    enabled: !!encId,
    staleTime: 30_000,
  });
}

interface UpdateCompanyArgs {
  encId: string;
  body: {
    tier?: string;
    status?: 'active' | 'inactive' | 'suspended';
    suspended_reason?: string;
  };
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async ({ encId, body }: UpdateCompanyArgs) => {
      const res = await apiClient.patch<
        { company: SuperCompanyDetail },
        UpdateCompanyArgs['body']
      >(`/super/companies/${encodeURIComponent(encId)}`, body);
      return res.data.company;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['super', 'companies'] });
      void qc.invalidateQueries({
        queryKey: ['super', 'companies', 'detail', vars.encId],
      });
      toast.success('Company updated.');
    },
    onError: () => {
      toast.error('Failed to update company.');
    },
  });
}

interface ImpersonateResult {
  accessToken: string;
  expiresAt: string;
  company: { id: string; name: string };
}

export function useImpersonateCompany() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const startImpersonation = useSuperAdminStore((s) => s.startImpersonation);
  const setAuthStoreAuthenticated = useAuthStore((s) => s.setAuthenticated);
  return useMutation({
    mutationFn: async (encId: string) => {
      const res = await apiClient.post<ImpersonateResult>(
        `/super/companies/${encodeURIComponent(encId)}/impersonate`,
      );
      return res.data;
    },
    onSuccess: async (data) => {
      // Wave I — impersonation state lives in the super-admin store. The
      // swap stashes the realm='super' access token and replaces it with
      // the realm='staff' impersonation token returned by the server.
      startImpersonation(data.accessToken, {
        enc_id: data.company.id,
        name: data.company.name,
      });
      // Tenant scope changed — drop every cache so the impersonated
      // session loads its own data fresh.
      qc.clear();
      // Hydrate authStore from the impersonation token so tenant routes
      // (which check authStore via ProtectedRoute) accept the session.
      try {
        const me = await apiClient.get<MeResult>(endpoints.auth.me);
        setAuthStoreAuthenticated(me.data.user, me.data.url_param_secret);
      } catch {
        // If /me fails the user will be bounced by ProtectedRoute on the
        // next render — non-fatal here.
      }
      toast.success(`Impersonating ${data.company.name}`);
      navigate('/collections/dashboard');
    },
    onError: () => {
      toast.error('Failed to start impersonation.');
    },
  });
}

// -- POST /super/companies (Wave H — provisioning) --------------------------

export interface CreateCompanyInput {
  name: string;
  subdomain: string;
  email: string;
  phone?: string | null;
  tier: 'starter' | 'professional' | 'enterprise';
  primary_admin: {
    name: string;
    email: string;
    username: string;
    password: string;
  };
  send_welcome_email: boolean;
  /**
   * Optional module subscription rows to seed alongside the company. Each
   * entry creates one row in `company_modules`. When omitted, no module
   * subscriptions are seeded (the company can have them added later via the
   * /modules endpoints on the detail page).
   */
  modules?: Array<{
    module: ModuleKey;
    tier: ModuleTierKey;
    mobile_dispatch_enabled?: boolean;
  }>;
}

export interface CreateCompanyResult {
  company: {
    enc_id: string;
    name: string;
    subdomain: string | null;
    tier: string | null;
    status: 'active' | 'inactive' | 'suspended';
  };
  admin: {
    enc_id: string;
    name: string;
    email: string;
    username: string;
  };
}

export function useCreateCompany() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (input: CreateCompanyInput) => {
      const res = await apiClient.post<CreateCompanyResult, CreateCompanyInput>(
        '/super/companies',
        input,
      );
      return res.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['super', 'companies'] });
      toast.success(`Company ${data.company.name} provisioned.`);
      navigate(`/super/companies/${encodeURIComponent(data.company.enc_id)}`);
    },
  });
}

export function useSystemHealth() {
  return useQuery<SystemHealth>({
    queryKey: ['super', 'system', 'health'],
    queryFn: async () => {
      const res = await apiClient.get<SystemHealth>('/super/system/health');
      return res.data;
    },
    refetchInterval: 30_000,
    staleTime: 25_000,
  });
}

// -- Admin management (Wave 2 — /super/admins) -----------------------------

export interface AdminListItem {
  enc_id: string;
  username: string;
  email: string;
  full_name: string;
  is_super_admin: boolean;
  status: 'active' | 'inactive' | 'suspended';
  last_login_at: string | null;
  created_at: string;
}

export interface ListAdminsFilters {
  page?: number;
  limit?: number;
  status?: 'active' | 'inactive' | 'suspended';
  search?: string;
}

interface ListAdminsResponse {
  admins: AdminListItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface CreateAdminInput {
  username: string;
  email: string;
  full_name: string;
  password: string;
  is_super_admin: boolean;
}

interface UpdateAdminStatusArgs {
  encId: string;
  body: { status: 'active' | 'inactive' | 'suspended' };
}

export interface ResetAdminPasswordInput {
  new_password: string;
  current_password?: string;
}

interface ResetAdminPasswordArgs {
  encId: string;
  body: ResetAdminPasswordInput;
}

export function useAllAdmins(
  filters: ListAdminsFilters = {},
  options: { enabled?: boolean } = {},
) {
  const params = new URLSearchParams();
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.status !== undefined) params.set('status', filters.status);
  if (filters.search !== undefined && filters.search.length > 0) {
    params.set('search', filters.search);
  }
  const qs = params.toString();
  const opts: UseQueryOptions<ListAdminsResponse> = {
    queryKey: ['super', 'admins', filters],
    queryFn: async () => {
      const res = await apiClient.get<ListAdminsResponse>(
        `/super/admins${qs ? `?${qs}` : ''}`,
      );
      return res.data;
    },
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  };
  return useQuery(opts);
}

export function useCreateAdmin() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (input: CreateAdminInput) => {
      const res = await apiClient.post<{ admin: AdminListItem }, CreateAdminInput>(
        '/super/admins',
        input,
      );
      return res.data.admin;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['super', 'admins'] });
      toast.success(`Admin ${data.username} created.`);
    },
  });
}

export function useUpdateAdminStatus() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async ({ encId, body }: UpdateAdminStatusArgs) => {
      const res = await apiClient.patch<
        { admin: AdminListItem },
        UpdateAdminStatusArgs['body']
      >(`/super/admins/${encodeURIComponent(encId)}/status`, body);
      return res.data.admin;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['super', 'admins'] });
      toast.success('Admin status updated.');
    },
    onError: () => {
      toast.error('Failed to update admin status.');
    },
  });
}

export function useResetAdminPassword() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async ({ encId, body }: ResetAdminPasswordArgs) => {
      const res = await apiClient.post<
        { admin_id: string },
        ResetAdminPasswordInput
      >(`/super/admins/${encodeURIComponent(encId)}/reset-password`, body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['super', 'admins'] });
      toast.success('Password reset.');
    },
    onError: () => {
      toast.error('Failed to reset password.');
    },
  });
}

// -- Audit log (Wave 3 — /super/audit-log) ---------------------------------

export interface AuditLogEntry {
  id: string;
  admin: { username: string; full_name: string };
  company: { name: string } | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  details: string | null;
  old_values: unknown | null;
  new_values: unknown | null;
  ip_address: string | null;
  created_at: string;
}

export interface ListAuditLogFilters {
  page?: number;
  limit?: number;
  admin_id?: string; // encrypted id
  company_id?: string; // encrypted id
  action?: string;
  date_from?: string; // ISO date — server accepts YYYY-MM-DD too.
  date_to?: string;
}

export interface ListAuditLogResponse {
  entries: AuditLogEntry[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export function useAuditLog(
  filters: ListAuditLogFilters = {},
  options: { enabled?: boolean } = {},
) {
  const params = new URLSearchParams();
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.admin_id !== undefined && filters.admin_id.length > 0) {
    params.set('admin_id', filters.admin_id);
  }
  if (filters.company_id !== undefined && filters.company_id.length > 0) {
    params.set('company_id', filters.company_id);
  }
  if (filters.action !== undefined && filters.action.length > 0) {
    params.set('action', filters.action);
  }
  if (filters.date_from !== undefined && filters.date_from.length > 0) {
    params.set('date_from', filters.date_from);
  }
  if (filters.date_to !== undefined && filters.date_to.length > 0) {
    params.set('date_to', filters.date_to);
  }
  const qs = params.toString();
  const opts: UseQueryOptions<ListAuditLogResponse> = {
    queryKey: ['super', 'audit-log', filters],
    queryFn: async () => {
      const res = await apiClient.get<ListAuditLogResponse>(
        `/super/audit-log${qs ? `?${qs}` : ''}`,
      );
      return res.data;
    },
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  };
  return useQuery(opts);
}

// -- Mobile submission logs (migration 141 — /super/mobile-submission-logs) --
//
// Cross-tenant structured audit of Dispatch receipt submissions + Skip
// Manager workflow actions. The actor is polymorphic (driver OR staff); the
// server returns a unified `actor_name`. `company_id` / `driver_id` filters
// are plaintext numeric — the super endpoint accepts numeric ids, not the
// encrypted enc_ids the audit-log endpoint uses.

export type MobileSubmissionApp = 'dispatch' | 'skip_manager';
export type MobileSubmissionActorType = 'driver' | 'staff';
export type MobileSubmissionOutcome =
  | 'success'
  | 'duplicate_blocked'
  | 'conflict_resolved'
  | 'error';

export interface MobileSubmissionLogEntry {
  id: number;
  uuid: string;
  company_id: number;
  company_name: string | null;
  actor_type: MobileSubmissionActorType;
  driver_id: number | null;
  staff_id: number | null;
  actor_name: string | null;
  app: MobileSubmissionApp;
  action: string;
  outcome: MobileSubmissionOutcome;
  booking_ref: string | null;
  receipt_id: number | null;
  customer_id: number | null;
  // MariaDB returns the JSON column as a string (JSON is LONGTEXT there), so
  // the renderer must tolerate either a string or an already-parsed object.
  context: unknown;
  ip_address: string | null;
  user_agent: string | null;
  request_id: string | null;
  created_at: string;
}

export interface ListMobileSubmissionLogsFilters {
  page?: number;
  limit?: number;
  company_id?: string;
  driver_id?: string;
  app?: MobileSubmissionApp;
  action?: string;
  outcome?: MobileSubmissionOutcome;
  date_from?: string;
  date_to?: string;
}

export interface ListMobileSubmissionLogsResponse {
  logs: MobileSubmissionLogEntry[];
  total: number;
  page: number;
  limit: number;
}

export function useMobileSubmissionLogs(
  filters: ListMobileSubmissionLogsFilters = {},
) {
  const params = new URLSearchParams();
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.company_id !== undefined && filters.company_id.length > 0) {
    params.set('company_id', filters.company_id);
  }
  if (filters.driver_id !== undefined && filters.driver_id.length > 0) {
    params.set('driver_id', filters.driver_id);
  }
  if (filters.app !== undefined) params.set('app', filters.app);
  if (filters.action !== undefined && filters.action.length > 0) {
    params.set('action', filters.action);
  }
  if (filters.outcome !== undefined) params.set('outcome', filters.outcome);
  if (filters.date_from !== undefined && filters.date_from.length > 0) {
    params.set('date_from', filters.date_from);
  }
  if (filters.date_to !== undefined && filters.date_to.length > 0) {
    params.set('date_to', filters.date_to);
  }
  const qs = params.toString();
  const opts: UseQueryOptions<ListMobileSubmissionLogsResponse> = {
    queryKey: ['super', 'mobile-submission-logs', filters],
    queryFn: async () => {
      const res = await apiClient.get<ListMobileSubmissionLogsResponse>(
        `/super/mobile-submission-logs${qs ? `?${qs}` : ''}`,
      );
      return res.data;
    },
    staleTime: 30_000,
  };
  return useQuery(opts);
}

// -- WB activity log (migration 142 — /super/wb-activity) -------------------
//
// Cross-tenant per-event audit of weighbridge ticket lifecycle transitions,
// carrying the richer event-specific `metadata` the super console renders.
// The `company_id` filter is plaintext numeric AND required by the endpoint —
// every query must carry one, so the page leaves the query disabled until a
// company is picked. The response shape mirrors the server envelope:
// `{ activity, total, page, per_page }` (not the `logs`/`limit` shape used by
// the mobile-submission-logs endpoint).

export const WB_ACTIVITY_EVENT_TYPES = [
  'ticket.created',
  'ticket.patched',
  'ticket.weight_captured',
  'ticket.snapshot_added',
  'ticket.completed',
  'ticket.voided',
  'ticket.deleted',
  'ticket.reissued',
  'ticket.advice_note_sent',
] as const;

export type WbActivityEventType = (typeof WB_ACTIVITY_EVENT_TYPES)[number];

export interface WbActivityEntry {
  id: number;
  company_id: number;
  company_name: string | null;
  entity_type: string;
  entity_id: number;
  entity_enc_id: string | null;
  entity_ref: string | null;
  event_type: string;
  actor_id: number | null;
  actor_name: string | null;
  // MariaDB returns the JSON `metadata` column as a string; the renderer
  // tolerates either a string or an already-parsed object.
  metadata: unknown;
  created_at: string;
  purge_after: string | null;
}

export interface ListWbActivityFilters {
  company_id?: number;
  event_type?: WbActivityEventType;
  from_date?: string;
  to_date?: string;
  page?: number;
  per_page?: number;
}

export interface ListWbActivityResponse {
  activity: WbActivityEntry[];
  total: number;
  page: number;
  per_page: number;
}

export function useWbActivity(
  filters: ListWbActivityFilters = {},
  options: { enabled?: boolean } = {},
) {
  const params = new URLSearchParams();
  if (filters.company_id !== undefined) {
    params.set('company_id', String(filters.company_id));
  }
  if (filters.event_type !== undefined) {
    params.set('event_type', filters.event_type);
  }
  if (filters.from_date !== undefined && filters.from_date.length > 0) {
    params.set('from_date', filters.from_date);
  }
  if (filters.to_date !== undefined && filters.to_date.length > 0) {
    params.set('to_date', filters.to_date);
  }
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.per_page !== undefined) {
    params.set('per_page', String(filters.per_page));
  }
  const qs = params.toString();
  const opts: UseQueryOptions<ListWbActivityResponse> = {
    queryKey: ['super', 'wb-activity', filters],
    queryFn: async () => {
      const res = await apiClient.get<ListWbActivityResponse>(
        `/super/wb-activity${qs ? `?${qs}` : ''}`,
      );
      return res.data;
    },
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  };
  return useQuery(opts);
}

// -- Company modules (module-based subscriptions) ---------------------------
//
// Mirrors the server-side companyModules.* module. Five endpoints:
//   GET    /super/companies/:enc_id/modules
//   POST   /super/companies/:enc_id/modules
//   PATCH  /super/companies/:enc_id/modules/:module
//   GET    /super/module-tier-limits
//   PATCH  /super/module-tier-limits/:module/:tier
//
// Per-module enc_id stays in the URL; nothing is ever passed in the body.

export const MODULE_KEYS = ['collections', 'skip', 'weighbridge'] as const;
export type ModuleKey = (typeof MODULE_KEYS)[number];

export const MODULE_TIER_KEYS = [
  'starter',
  'professional',
  'enterprise',
] as const;
export type ModuleTierKey = (typeof MODULE_TIER_KEYS)[number];

export interface ModuleTierLimits {
  max_staff: number | null;
  max_drivers: number | null;
  max_monthly_jobs: number | null;
  max_monthly_tickets: number | null;
  max_active_inventory: number | null;
  max_weighbridges: number | null;
  max_cameras_per_wb: number | null;
  max_monthly_messages: number | null;
}

export interface CompanyModuleItem {
  module: ModuleKey;
  tier: ModuleTierKey;
  is_active: boolean;
  mobile_dispatch_enabled: boolean;
  limits: ModuleTierLimits;
}

export type ModuleTierLimitsCatalogue = {
  [M in ModuleKey]: {
    [T in ModuleTierKey]: ModuleTierLimits;
  };
};

export interface AddCompanyModuleInput {
  module: ModuleKey;
  tier: ModuleTierKey;
  mobile_dispatch_enabled?: boolean;
}

export interface UpdateCompanyModuleInput {
  tier?: ModuleTierKey;
  is_active?: boolean;
  mobile_dispatch_enabled?: boolean;
}

export interface UpdateModuleTierLimitsInput {
  max_staff?: number | null;
  max_drivers?: number | null;
  max_monthly_jobs?: number | null;
  max_monthly_tickets?: number | null;
  max_active_inventory?: number | null;
  max_weighbridges?: number | null;
  max_cameras_per_wb?: number | null;
  max_monthly_messages?: number | null;
}

interface ListCompanyModulesResponse {
  modules: CompanyModuleItem[];
}

export function useCompanyModules(encId: string | undefined) {
  return useQuery<CompanyModuleItem[]>({
    queryKey: ['super', 'companies', 'modules', encId],
    queryFn: async () => {
      if (!encId) throw new Error('enc_id required');
      const res = await apiClient.get<ListCompanyModulesResponse>(
        `/super/companies/${encodeURIComponent(encId)}/modules`,
      );
      return res.data.modules;
    },
    enabled: !!encId,
    staleTime: 30_000,
  });
}

interface AddCompanyModuleArgs {
  encId: string;
  body: AddCompanyModuleInput;
}

export function useAddCompanyModule() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async ({ encId, body }: AddCompanyModuleArgs) => {
      const res = await apiClient.post<
        { module: CompanyModuleItem },
        AddCompanyModuleInput
      >(`/super/companies/${encodeURIComponent(encId)}/modules`, body);
      return res.data.module;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['super', 'companies', 'modules', vars.encId],
      });
      toast.success('Module added.');
    },
    onError: () => {
      toast.error('Failed to add module.');
    },
  });
}

interface UpdateCompanyModuleArgs {
  encId: string;
  module: ModuleKey;
  body: UpdateCompanyModuleInput;
}

export function useUpdateCompanyModule() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async ({ encId, module, body }: UpdateCompanyModuleArgs) => {
      const res = await apiClient.patch<
        { module: CompanyModuleItem },
        UpdateCompanyModuleInput
      >(
        `/super/companies/${encodeURIComponent(encId)}/modules/${encodeURIComponent(module)}`,
        body,
      );
      return res.data.module;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['super', 'companies', 'modules', vars.encId],
      });
      toast.success('Module updated.');
    },
    onError: () => {
      // Toast suppression for 422 violations is handled by the caller —
      // they need to surface the per-field breakdown inline.
    },
  });
}

export function useModuleTierLimits() {
  return useQuery<ModuleTierLimitsCatalogue>({
    queryKey: ['super', 'module-tier-limits'],
    queryFn: async () => {
      const res = await apiClient.get<ModuleTierLimitsCatalogue>(
        '/super/module-tier-limits',
      );
      return res.data;
    },
    staleTime: 5 * 60_000,
  });
}

interface UpdateModuleTierLimitsArgs {
  module: ModuleKey;
  tier: ModuleTierKey;
  body: UpdateModuleTierLimitsInput;
}

export function useUpdateModuleTierLimits() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async ({ module, tier, body }: UpdateModuleTierLimitsArgs) => {
      const res = await apiClient.patch<
        {
          module: ModuleKey;
          tier: ModuleTierKey;
          limits: ModuleTierLimits;
        },
        UpdateModuleTierLimitsInput
      >(
        `/super/module-tier-limits/${encodeURIComponent(module)}/${encodeURIComponent(tier)}`,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['super', 'module-tier-limits'] });
      toast.success('Tier limits updated.');
    },
    onError: () => {
      toast.error('Failed to update tier limits.');
    },
  });
}

/**
 * Best-effort extractor for the 422 TIER_DOWNGRADE_BLOCKED response body.
 * Returns the violations array if the error matches that shape, or null.
 * Callers (modules section, tier editor) use this to surface a per-field
 * breakdown inline rather than relying on the generic toast path.
 */
export interface TierDowngradeViolation {
  field: string;
  current: number;
  limit: number;
}

export function extractDowngradeViolations(
  err: unknown,
): TierDowngradeViolation[] | null {
  if (err === null || typeof err !== 'object') return null;
  const ax = err as {
    response?: {
      status?: number;
      data?: {
        error?: string;
        violations?: unknown;
      };
    };
  };
  if (ax.response?.status !== 422) return null;
  const v = ax.response.data?.violations;
  if (!Array.isArray(v)) return null;
  return v.filter(
    (row): row is TierDowngradeViolation =>
      row !== null &&
      typeof row === 'object' &&
      typeof (row as { field?: unknown }).field === 'string' &&
      typeof (row as { current?: unknown }).current === 'number' &&
      typeof (row as { limit?: unknown }).limit === 'number',
  );
}

// ---------------------------------------------------------------------------
// Global product groups (migration 169)
//
// Platform-managed grouping headers (wb_product_groups, company_id NULL) that
// every tenant sees. Tenants opt out per-group; only super admins create /
// edit / soft-delete the globals here. `product_count` is how many live
// products across all tenants currently reference the group.
// ---------------------------------------------------------------------------

export interface SuperProductGroup {
  enc_id: string;
  company_id: number | null;
  name: string;
  code: string;
  sort_order: number;
  is_active: boolean;
  is_global: boolean;
  is_excluded: boolean;
  product_count?: number;
  created_at: string;
  updated_at: string;
}

interface ListGlobalGroupsResponse {
  groups: SuperProductGroup[];
}

export interface CreateGlobalGroupInput {
  name: string;
  code: string;
  sort_order?: number;
}

export interface UpdateGlobalGroupInput {
  name?: string;
  code?: string;
  sort_order?: number;
  is_active?: boolean;
}

export function useSuperProductGroups() {
  return useQuery<ListGlobalGroupsResponse>({
    queryKey: ['super', 'product-groups'],
    queryFn: async () => {
      const res = await apiClient.get<ListGlobalGroupsResponse>('/super/product-groups');
      return res.data;
    },
    staleTime: 60_000,
  });
}

function useInvalidateSuperGroups() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['super', 'product-groups'] });
  };
}

export function useCreateGlobalGroup() {
  const invalidate = useInvalidateSuperGroups();
  const toast = useToast();
  return useMutation<{ group: SuperProductGroup }, Error, CreateGlobalGroupInput>({
    mutationFn: async (body) => {
      const res = await apiClient.post<{ group: SuperProductGroup }, CreateGlobalGroupInput>(
        '/super/product-groups',
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Global group created.');
    },
  });
}

export function useUpdateGlobalGroup() {
  const invalidate = useInvalidateSuperGroups();
  const toast = useToast();
  return useMutation<
    { group: SuperProductGroup },
    Error,
    { encId: string; body: UpdateGlobalGroupInput }
  >({
    mutationFn: async ({ encId, body }) => {
      const res = await apiClient.patch<{ group: SuperProductGroup }, UpdateGlobalGroupInput>(
        `/super/product-groups/${encodeURIComponent(encId)}`,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Global group updated.');
    },
  });
}

export function useDeleteGlobalGroup() {
  const invalidate = useInvalidateSuperGroups();
  const toast = useToast();
  return useMutation<{ deleted: true }, Error, string>({
    mutationFn: async (encId) => {
      const res = await apiClient.delete<{ deleted: true }>(
        `/super/product-groups/${encodeURIComponent(encId)}`,
      );
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Global group deleted.');
    },
  });
}
